import { AnimationUtils } from '../models/AnimationTypes';

import type { Animation, Overlay } from '../models/AnimationTypes';

/**
 * Pure-function serializer that converts native dip/crossfade animations
 * into Creatomate-compatible color_overlay / composition wrapper JSON.
 *
 * Used in Timeline.toPreviewJSON and Timeline.toRenderJSON only.
 * toStoredJSON preserves native animation types unchanged.
 *
 * No state, no store access, no side effects.
 */

// ─── Public API ──────────────────────────────────────────────────────────────

/**
 * Transforms a serialized element JSON: if it contains dip or crossfade
 * animations, converts them into Creatomate-compatible output.
 * Elements without fade animations pass through unchanged.
 */
export function serializeElement(
  elementJSON: Record<string, any>,
): Record<string, any> {
  const animations: Animation[] = elementJSON.animations ?? [];
  const fadeEnter = animations.find(
    a =>
      AnimationUtils.isFadeOverlayType(a.type) &&
      (a.time === 'start' || a.time === 0),
  );
  const fadeExit = animations.find(
    a => AnimationUtils.isFadeOverlayType(a.type) && a.time === 'end',
  );

  if (!fadeEnter && !fadeExit) return elementJSON;

  // Strip fade animations from the output — they'll be represented as overlays
  const remainingAnimations = animations.filter(
    a => !AnimationUtils.isFadeOverlayType(a.type),
  );

  const hasCrossfade =
    fadeEnter?.type === 'crossfade' || fadeExit?.type === 'crossfade';

  if (hasCrossfade) {
    return buildCrossfadeComposition(
      elementJSON,
      remainingAnimations,
      fadeEnter,
      fadeExit,
    );
  }

  // Dip only — inject color_overlay directly, no composition wrapper
  return buildDipElement(elementJSON, remainingAnimations, fadeEnter, fadeExit);
}

/**
 * Checks whether an element JSON contains fade overlay animations.
 */
export function hasFadeAnimation(elementJSON: Record<string, any>): boolean {
  const animations: Animation[] = elementJSON.animations ?? [];
  return animations.some(a => AnimationUtils.isFadeOverlayType(a.type));
}

// ─── Dip (no composition wrapper) ────────────────────────────────────────────

/**
 * Builds color_overlay array for a dip enter effect.
 */
export function buildDipEnterOverlay(duration: number): Overlay[] {
  return [
    { time: 0, easing: 'quadratic-in', value: 'rgba(0, 0, 0, 1)' },
    { time: duration, easing: 'quadratic-in', value: 'rgba(0, 0, 0, 0)' },
  ];
}

/**
 * Builds color_overlay array for a dip exit effect.
 */
export function buildDipExitOverlay(
  elementDuration: number,
  duration: number,
): Overlay[] {
  return [
    {
      time: elementDuration - duration,
      easing: `quadratic-out${duration}`,
      value: 'rgba(0, 0, 0, 0)',
    },
    {
      time: 'end',
      easing: `quadratic-out${duration}`,
      value: 'rgba(0, 0, 0, 1)',
    },
  ];
}

function buildDipElement(
  elementJSON: Record<string, any>,
  remainingAnimations: Animation[],
  fadeEnter: Animation | undefined,
  fadeExit: Animation | undefined,
): Record<string, any> {
  const elementDuration =
    typeof elementJSON.duration === 'number' ? elementJSON.duration : 0;
  const enterDuration = fadeEnter?.duration ?? 1;
  const exitDuration = fadeExit?.duration ?? 1;

  const overlays: Overlay[] = [
    ...(fadeEnter ? buildDipEnterOverlay(enterDuration) : []),
    ...(fadeExit ? buildDipExitOverlay(elementDuration, exitDuration) : []),
  ];

  return {
    ...elementJSON,
    ...(remainingAnimations.length > 0
      ? { animations: remainingAnimations }
      : {}),
    ...(remainingAnimations.length === 0 && elementJSON.animations
      ? { animations: undefined }
      : {}),
    color_overlay: overlays,
  };
}

// ─── Crossfade (composition wrapper) ─────────────────────────────────────────

/**
 * Builds color_overlay array for a crossfade on the inner element.
 */
export function buildCrossfadeOverlay(
  elementDuration: number,
  enterDuration: number | undefined,
  exitDuration: number | undefined,
): Overlay[] {
  const overlays: Overlay[] = [];

  if (enterDuration != null) {
    overlays.push(
      { time: 0, easing: 'ease-in', value: 'rgba(0, 0, 0, 1)' },
      { time: enterDuration, easing: 'ease-in', value: 'rgba(0, 0, 0, 0)' },
    );
  }

  if (exitDuration != null) {
    overlays.push(
      {
        time: elementDuration - exitDuration,
        easing: 'ease-out',
        value: 'rgba(0, 0, 0, 0)',
      },
      { time: 'end', easing: 'ease-out', value: 'rgba(0, 0, 0, 1)' },
    );
  }

  return overlays;
}

/**
 * Builds the opacity keyframe array for the black shape element
 * inside a crossfade composition.
 */
export function buildShapeOpacity(
  compDuration: number,
  enterDuration: number | undefined,
  exitDuration: number | undefined,
): Array<{ time: number; value: number | string }> {
  const keyframes: Array<{ time: number; value: number | string }> = [];

  if (enterDuration != null) {
    keyframes.push(
      { time: 0, value: 0 },
      { time: enterDuration, value: '100%' },
    );
  }

  if (exitDuration != null) {
    keyframes.push(
      { time: compDuration - exitDuration, value: '100%' },
      { time: compDuration, value: 0 },
    );
  }

  return keyframes;
}

/**
 * Wraps an element JSON in a crossfade composition with a black shape overlay.
 * The composition extends before/after the element to accommodate the fade.
 */
export function wrapElementInFadeComposition(
  elementJSON: Record<string, any>,
  remainingAnimations: Animation[],
  fadeEnter: Animation | undefined,
  fadeExit: Animation | undefined,
): Record<string, any> {
  const elementDuration = parseFloat(elementJSON.duration) || 0;
  const enterDuration = fadeEnter?.duration ?? 0;
  const exitDuration = fadeExit?.duration ?? 0;
  const elementTime = parseFloat(elementJSON.time) || 0;

  // The composition normally starts `enterDuration` before the element so the
  // enter fade can play against whatever precedes it. Clamp that lead-in to the
  // element's own start time: an element at (or within `enterDuration` of) the
  // timeline start has no room to shift earlier, and a negative composition
  // `time` is invalid — Creatomate clamps/drops it and the element misrenders.
  // When there's no room, the enter fade plays over the element's own start.
  const enterLead = Math.min(enterDuration, elementTime);
  const compDuration = elementDuration + enterLead + exitDuration;

  const colorOverlay = buildCrossfadeOverlay(
    elementDuration,
    fadeEnter ? enterDuration : undefined,
    fadeExit ? exitDuration : undefined,
  );
  const shapeOpacity = buildShapeOpacity(
    compDuration,
    fadeEnter ? enterDuration : undefined,
    fadeExit ? exitDuration : undefined,
  );

  // Build inner element — stripped of track/time (relative within composition)
  const {
    track: _track,
    time: _time,
    z_index: _zIndex,
    ...innerProps
  } = elementJSON;

  const innerElement: Record<string, any> = {
    ...innerProps,
    track: 2,
    time: enterLead,
    duration: elementDuration,
    ...(remainingAnimations.length > 0
      ? { animations: remainingAnimations }
      : {}),
    color_overlay: colorOverlay,
  };
  // Remove native animations array if empty
  if (remainingAnimations.length === 0) {
    delete innerElement.animations;
  }

  const shapeElement: Record<string, any> = {
    type: 'shape',
    track: 1,
    time: 0,
    width: '100%',
    height: '100%',
    x_anchor: '50%',
    y_anchor: '50%',
    path: 'M 0 0 L 100 0 L 100 100 L 0 100 L 0 0 Z',
    fill_color: 'black',
    opacity: shapeOpacity,
  };

  return {
    type: 'composition',
    track: elementJSON.track,
    time: elementTime - enterLead,
    duration: compDuration,
    ...(elementJSON.z_index != null ? { z_index: elementJSON.z_index } : {}),
    elements: [shapeElement, innerElement],
  };
}

function buildCrossfadeComposition(
  elementJSON: Record<string, any>,
  remainingAnimations: Animation[],
  fadeEnter: Animation | undefined,
  fadeExit: Animation | undefined,
): Record<string, any> {
  return wrapElementInFadeComposition(
    elementJSON,
    remainingAnimations,
    fadeEnter,
    fadeExit,
  );
}
